import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { WorkbenchRef } from '@jabberr/core';
import { api, ApiError, errorMessage } from '../lib/api.js';

/** True on phone widths (the same 800px breakpoint as the top bar). Read once per render; good enough for links. */
export function isNarrow(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 800px)').matches;
}

/** A link to another site. On a phone it is an ordinary same-window link, so the iPhone app's web view sees a plain
 *  navigation it can hand to Safari (a new window is not something an embedded web view can open); on a wide screen
 *  it opens a new tab. */
export function ExternalLink({ href, className, title, children }: { href: string; className?: string; title?: string; children: ReactNode }) {
  const narrow = isNarrow();
  return (
    <a className={className} href={href} title={title} rel="noopener noreferrer" {...(narrow ? {} : { target: '_blank' })}>
      {children}
    </a>
  );
}

/** The not-linked note with the way to the project page, where an owner links it (the Workbench project field). */
export function UnlinkedNote({ projectId }: { projectId: string }) {
  return (
    <p className="workbench-note" role="status">
      {UNLINKED_MESSAGE} <Link to={`/p/${projectId}#workbench-project`}>Go to the project page</Link>
    </p>
  );
}

export const UNLINKED_MESSAGE = 'This project is not linked to the Workbench yet; an owner links it on the project page.';

/** Save to the Workbench (OPEN-34) for one saved document, in every state a person can meet: already saved (a link to
 *  the item), ready to save, saving, and not possible yet because the project is not linked (with the way to the place
 *  an owner links it). Renders nothing when this Jabberr or this kind of document cannot go to the Workbench. */
export function WorkbenchSave({
  projectId,
  turnId,
  workbench,
  canSave,
  linked,
  disabled,
  primary = true,
  hideNote = false,
  onSaved,
}: {
  projectId: string;
  turnId: string;
  workbench: WorkbenchRef | null | undefined;
  canSave: boolean;
  /** Whether the project has a Workbench project linked; null while unknown (the button shows and the server decides). */
  linked: boolean | null;
  disabled?: boolean;
  primary?: boolean;
  /** The page already says once that the project is not linked (a list); the button still turns off. */
  hideNote?: boolean;
  onSaved: (ref: WorkbenchRef) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlinked, setUnlinked] = useState(false);

  if (workbench) {
    return (
      <ExternalLink className="btn" href={workbench.url} title="Open the item in the Anchor Workbench">
        Workbench #{workbench.number}
      </ExternalLink>
    );
  }
  if (!canSave) return null;
  const notLinked = linked === false || unlinked;

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const { workbench: ref } = await api.outputs.saveToWorkbench(projectId, turnId);
      onSaved(ref);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'workbench_project_unset') setUnlinked(true);
      else if (err instanceof ApiError && err.code === 'route_not_found') setError('Saving to the Workbench is not set up on this Jabberr yet.');
      else setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="workbench-save">
      <button
        type="button"
        className={`btn ${primary ? 'btn-primary' : ''}`}
        onClick={() => void save()}
        disabled={disabled || saving || notLinked}
        title="Save this document as an item in the Anchor Workbench"
      >
        {saving ? 'Saving' : 'Save to the Workbench'}
      </button>
      {unlinked || (notLinked && !hideNote) ? (
        <UnlinkedNote projectId={projectId} />
      ) : null}
      {error ? (
        <p className="workbench-note workbench-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
